import { Optional } from '@core/models/global-entities/global-entities';
import { dropRight } from 'lodash';

const MAX_WIDTH = 1000;
const MAX_HEIGHT = 1000;
export const IMG_EXTENSION = ['jpg', 'jpeg', 'gif', 'png','.jpg','.jpeg', '.gif', '.png'];
export const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024;
export const ALLOWED_EXTENSIONS = [
  'jpg',
  'jpeg',
  'png',
  'doc',
  'docx',
  'pdf',
  'xls',
  'xlsx',
  'csv',
  'txt',
  '.jpg',
  '.jpeg',
  '.png',
  '.doc',
  '.docx',
  '.pdf',
  '.xls',
  '.xlsx',
  '.csv',
  '.txt',
];
export const BLANK_IMAGE =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+P9fAwAFLQIoXRULwAAAAABJRU5ErkJggg==';

export class ImageUtil {
  getMaxSizeLimit(): number {
    return MAX_ATTACHMENT_SIZE;
  }

  getBlankImage() {
    return BLANK_IMAGE;
  }

  checkMaxSize(files: Set<File>): boolean {
    const total = Array.from(files).reduce((acc, f) => acc + f.size, 0);
    return total > this.getMaxSizeLimit();
  }
  // generic compress function for image type file
  async compress(
    f: File,
    toDataURL = false,
    maxWidth = MAX_WIDTH,
    maxHeight = MAX_HEIGHT
  ) {
    // caluclate max resolution of output file or data url;
    const maxResolution = maxWidth * maxHeight;
    // if not image return error
    if (!this.isImage(f)) {
      throw new Error('NotAnImage');
    } else {
      try {
        // read file data
        const dataURL = await this.readAsDataURL(f).then((d) => d.dataURL);
        // load data url into an image element for resizing.
        const img = await this.readAsImage(dataURL);
        const aspectRatio = img.width / img.height;
        const canvas = document.createElement('canvas');
        // check if image size is within the resolution limit.
        if (img.width * img.height <= maxResolution) {
          return toDataURL
            ? await this.readAsDataURL(f).then((d) => d.dataURL)
            : f;
        } else {
          // scale down image wrt to width
          let targetWidth = maxWidth;
          let targetHeight = targetWidth / aspectRatio;

          // scale down image wrt to height
          if (img.width < img.height) {
            targetHeight = maxHeight;
            targetWidth = targetHeight * aspectRatio;
          }
          canvas.width = targetWidth;
          canvas.height = targetHeight;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
          // return data url or file depending on setting.
          return toDataURL
            ? ctx.canvas.toDataURL(f.type, 1)
            : await this.canvasToFile(ctx.canvas, f.name);
        }
      } catch (err) {
        throw err;
      }
    }
  }

  canvasToFile(canvas, fileName): Promise<File> {
    return new Promise((resolve) => {
      const jpegType = 'image/jpeg';
      canvas.toBlob(
        (blob) =>
          resolve(
            new File([blob], fileName, {
              type: jpegType,
              lastModified: Date.now(),
            })
          ),
        jpegType,
        1
      );
    });
  }

  readAsImage(dataURL): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      if (!dataURL) {
        return reject('EmptyArgument');
      }
      const img = new Image();
      img.onload = () => resolve(img);
      img.src = dataURL;
    });
  }

  readAsDataURL(f: File): Promise<any> {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.onload = (event) =>
        resolve({
          dataURL: event.target['result'],
          fileName: f.name,
        });
      fileReader.onerror = reject;
      fileReader.readAsDataURL(f);
    });
  }

  isImage(f: File): boolean {
    return f && f.type.indexOf('image') >= 0;
  }

  isValidExtension(f: File | string): boolean {
    let filename: string;
    if (f instanceof File) {
      filename = f.name;
    } else {
      filename = f;
    }
    return (
      ALLOWED_EXTENSIONS.indexOf(this.getExtension(filename).toLowerCase()) >= 0
    );
  }

  isImageFromName(fileName: string, extensionAdded?: string): boolean {
    if (!fileName) {
      return false;
    }
    const extension = this.getExtension(fileName, extensionAdded);
    return IMG_EXTENSION.indexOf(extension) >= 0;
  }

  getExtension(fileName: string, extensionAdded?: string): Optional<string> {
    if (!fileName) {
      return null;
    }
    const [extension] = fileName.split('.').slice(-1);
    return extensionAdded?.toLowerCase() ?? extension.toLowerCase();
  }

  getPureName(f: File | string): string {
    const filename = (f instanceof File ? f.name : f)?.trim();
    if (filename.length) {
      const parts = filename.split('.');
      return parts.length > 1 ? dropRight(parts).join('.') : filename;
    }

    return filename;
  }

  getIconForFile(fileName: string, extensionAdded?: string): string {
    const ext = this.getExtension(fileName, extensionAdded)?.replace(/^\./, '');
    const pathname = '/assets/images/fileTypes/';
    let iconFile = 'file.png';
    switch (ext) {
      case 'pdf':
        iconFile = 'pdf.png';
        break;
      case 'doc':
      case 'docx':
        iconFile = 'doc.png';
        break;
      case 'xls':
      case 'xlsx':
        iconFile = 'xls.png';
        break;
      case 'jpg':
      case 'jpeg':
        iconFile = 'jpg.png';
        break;
      case 'gif':
        iconFile = 'gif.png';
        break;
      case 'png':
        iconFile = 'png.png';
        break;
      case 'txt':
        iconFile = 'txt.png';
        break;
    }
    return pathname + iconFile;
  }
}
